// UserLogin.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Form, Button, Card } from 'react-bootstrap';
import { Eye, EyeOff, ArrowLeft } from 'lucide-react';
import { toast } from 'react-toastify';
import authService from '../services/authService';

const UserLogin = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isSignup = location.pathname === '/signup';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const successMessage = location.state?.message;

  useEffect(() => {
    if (successMessage) {
      toast.success(successMessage, {
        position: 'bottom-left',
        autoClose: 3000,
      });
    }
  }, [successMessage]);

  const handleAuth = async () => {
    if (!email || !password) {
      toast.error('Email and password are required', {
        position: 'bottom-left',
        autoClose: 3000,
      });
      return;
    }

    if (isSignup) {
      // Signup and enter the authenticated area immediately
      setLoading(true);
      try {
        await authService.register(email, password);
        toast.success('Registration successful!', {
          position: 'bottom-left',
          autoClose: 2000,
        });
        navigate('/home');
      } catch (err) {
        const errorMsg =
          err.message || 'Registration failed. Please try again.';
        if (
          errorMsg.toLowerCase().includes('already') ||
          errorMsg.toLowerCase().includes('exist')
        ) {
          toast.warning(
            'This email is already registered. Please login.',
            {
              position: 'bottom-left',
              autoClose: 6000,
            },
          );
        } else {
          toast.error(errorMsg, {
            position: 'bottom-left',
            autoClose: 4000,
          });
        }
      } finally {
        setLoading(false);
      }
    } else {
      // Login flow
      setLoading(true);
      try {
        await authService.login(email, password);
        toast.success('Login successful!', {
          position: 'bottom-left',
          autoClose: 1500,
        });
        navigate('/home');
      } catch (err) {
        const raw =
          err?.message || 'Login failed. Please check your credentials.';
        const errorMsg = raw.trim();
        const lower = errorMsg.toLowerCase();
        if (lower.includes('network')) {
          toast.error(
            'Network error. Please check your connection and try again.',
            { position: 'bottom-left', autoClose: 4500 },
          );
        } else if (lower.includes('timeout')) {
          toast.error('Request timed out. Retry in a moment.', {
            position: 'bottom-left',
            autoClose: 4500,
          });
        } else if (lower.includes('password')) {
          toast.error('Incorrect password. Try again or use Forgot Password.', {
            position: 'bottom-left',
            autoClose: 4500,
          });
        } else if (lower.includes('user') && lower.includes('not')) {
          toast.error('User not found. Please sign up first.', {
            position: 'bottom-left',
            autoClose: 4500,
          });
        } else {
          toast.error(errorMsg, { position: 'bottom-left', autoClose: 4000 });
        }
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        backgroundColor: '#f8f9fa',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '20px',
      }}
    >
      <div className='w-100' style={{ maxWidth: '450px' }}>
        <Card
          style={{
            border: 'none',
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            backgroundColor: '#ffffff',
          }}
        >
          <Card.Body className='p-4'>
            <div
              className='d-flex align-items-center justify-content-center mb-4'
              style={{ position: 'relative' }}
            >
              <Button
                variant='link'
                onClick={() => navigate('/home')}
                className='p-0'
                style={{
                  position: 'absolute',
                  left: 0,
                  color: '#000000ff',
                }}
                aria-label='Go back'
              >
                <ArrowLeft size={22} />
              </Button>
              <h4
                className='mb-0'
                style={{ fontWeight: '600', color: '#212529' }}
              >
                {isSignup ? 'Create Account' : 'Welcome Back'}
              </h4>
            </div>

            <Form
              onSubmit={(e) => {
                e.preventDefault();
                handleAuth();
              }}
            >
              <Form.Group className='mb-3'>
                <Form.Label
                  style={{
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#212529',
                  }}
                >
                  Email
                </Form.Label>
                <Form.Control
                  type='email'
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAuth();
                    }
                  }}
                  placeholder=''
                  autoComplete='email'
                  required
                  style={{
                    padding: '12px 16px',
                    fontSize: '15px',
                    borderRadius: '8px',
                    border: '1px solid #dee2e6',
                    backgroundColor: '#e8f0fe',
                  }}
                />
              </Form.Group>

              <Form.Group className='mb-3'>
                <Form.Label
                  style={{
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#212529',
                  }}
                >
                  Password
                </Form.Label>
                <div style={{ position: 'relative' }}>
                  <Form.Control
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAuth();
                      }
                    }}
                    placeholder=''
                    autoComplete='current-password'
                    required
                    style={{
                      padding: '12px 45px 12px 16px',
                      fontSize: '15px',
                      borderRadius: '8px',
                      border: '1px solid #dee2e6',
                      backgroundColor: '#e8f0fe',
                    }}
                  />
                  <Button
                    variant='link'
                    onClick={() => setShowPassword(!showPassword)}
                    style={{
                      position: 'absolute',
                      right: '10px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: '#6c757d',
                      padding: '0',
                      border: 'none',
                      background: 'transparent',
                    }}
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </Button>
                </div>
              </Form.Group>

              {!isSignup && (
                <div className='text-end mb-3'>
                  <span style={{ fontSize: '14px', color: '#6c757d' }}>
                    Forgot Password?
                  </span>
                  <Button
                    variant='link'
                    onClick={() => navigate('/chat')}
                    className='p-0 ms-3 text-decoration-none fw-semibold'
                    style={{ fontSize: '14px', color: '#0d6efd' }}
                  >
                    Customer Service
                  </Button>
                </div>
              )}

              <Button
                type='button'
                className='w-100 mb-3'
                onClick={handleAuth}
                disabled={loading}
                style={{
                  padding: '12px',
                  fontSize: '16px',
                  fontWeight: '500',
                  borderRadius: '8px',
                  backgroundColor: '#0d6efd',
                  border: 'none',
                }}
              >
                {loading ? 'Loading...' : isSignup ? 'Sign Up' : 'Log In'}
              </Button>

              <div className='text-center' style={{ marginTop: '16px' }}>
                <p style={{ fontSize: '14px', color: '#6c757d', margin: '0' }}>
                  {isSignup ? 'Already have an account? ' : 'New here? '}
                  <Button
                    variant='link'
                    onClick={() => {
                      navigate(isSignup ? '/login' : '/signup');
                      // Keep email and password when switching pages
                    }}
                    className='p-0 text-decoration-none'
                    style={{
                      fontSize: '14px',
                      color: '#0d6efd',
                      fontWeight: '500',
                      verticalAlign: 'baseline',
                    }}
                  >
                    {isSignup ? 'Log In' : 'Sign Up'}
                  </Button>
                </p>
              </div>
            </Form>
          </Card.Body>
        </Card>
      </div>
    </div>
  );
};

export default UserLogin;
